import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './Blogs.css';
import { blogs as allBlogs } from '../../data/blogs';

// Get unique categories for filter
const categories = ['All', ...new Set(allBlogs.map(blog => blog.category))];

// Get all unique tags
const allTags = [...new Set(allBlogs.flatMap(blog => blog.tags))];

const Blogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('date');

  // Toggle tag selection
  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Filter and sort blogs
  const filteredBlogs = allBlogs
    .filter(blog => {
      const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;
      const matchesTags = selectedTags.length === 0 || 
                         selectedTags.some(tag => blog.tags.includes(tag));
      return matchesSearch && matchesCategory && matchesTags;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.date) - new Date(a.date);
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      } else if (sortBy === 'readTime') {
        return parseInt(a.readTime) - parseInt(b.readTime);
      }
      return 0;
    });

  return (
    <div className="blogs-page py-5">
      <Container>
        <div className="text-center mb-5">
          <h1 className="display-5 fw-bold mb-3">Blogs</h1>
          <p className="lead text-muted">Learn about online earning, freelancing, and digital marketing in Marathi</p>
        </div>
        
        {/* Search and Filter Section */}
        <div className="mb-5">
          <Row className="g-3">
            <Col md={6} lg={4}>
              <div className="search-box">
                <Form.Control
                  type="text"
                  placeholder="Search blogs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button variant="outline-secondary"><i className="bi bi-search"></i></Button>
              </div>
            </Col>
            <Col md={4} lg={3}>
              <Form.Select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'All' ? 'All Categories' : category}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} lg={3}>
              <Form.Select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">Sort by Date</option>
                <option value="title">Sort by Title</option>
                <option value="readTime">Sort by Read Time</option>
              </Form.Select>
            </Col>
            <Col md={2} className="d-grid">
              <Button 
                variant="outline-secondary" 
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedTags([]);
                  setSortBy('date');
                }}
              >
                Reset
              </Button>
            </Col>
          </Row>

          {/* Tags Filter */}
          <div className="mt-4">
            <div className="d-flex flex-wrap align-items-center mb-2">
              <span className="me-2"><i className="bi bi-tags me-1"></i>Filter by tags:</span>
              {allTags.slice(0, 5).map(tag => (
                <Button
                  key={tag}
                  variant={selectedTags.includes(tag) ? 'primary' : 'outline-secondary'}
                  size="sm"
                  className="me-2 mb-2"
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </Button>
              ))}
              {allTags.length > 5 && (
                <Button variant="link" size="sm" className="text-decoration-none">
                  +{allTags.length - 5} more
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div className="mb-4">
            <h6 className="mb-2">Selected Tags:</h6>
            <div className="d-flex flex-wrap">
              {selectedTags.map(tag => (
                <Badge 
                  key={tag} 
                  bg="primary" 
                  className="me-2 mb-2 d-flex align-items-center"
                  style={{ fontSize: '0.85rem', padding: '0.35em 0.65em' }}
                >
                  {tag}
                  <button 
                    className="btn-close btn-close-white ms-2" 
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => toggleTag(tag)}
                    aria-label={`Remove ${tag}`}
                  />
                </Badge>
              ))}
              <Button 
                variant="link" 
                size="sm" 
                className="text-decoration-none p-0 ms-2 mb-2 align-self-center"
                onClick={() => setSelectedTags([])}
              >
                Clear all
              </Button>
            </div>
          </div>
        )}

        {/* Blogs Grid */}
        {filteredBlogs.length > 0 ? (
          <Row xs={1} md={2} lg={3} className="g-4">
            {filteredBlogs.map((blog) => (
              <Col key={blog.id}>
                <Card className="h-100 blog-card">
                  <div className="blog-image-container">
                    <Card.Img 
                      variant="top" 
                      src={blog.image} 
                      alt={blog.title}
                      className="blog-image"
                    />
                    <div className="category-badge">
                      {blog.category}
                    </div>
                  </div>
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted"><i className="bi bi-calendar3 me-1"></i>{new Date(blog.date).toLocaleDateString('en-IN')}</small>
                      <small className="text-muted"><i className="bi bi-clock me-1"></i>{blog.readTime}</small>
                    </div>
                    <Card.Title className="h5">{blog.title}</Card.Title>
                    <Card.Text className="flex-grow-1">
                      {blog.excerpt}
                    </Card.Text>
                    <div className="mt-auto">
                      <div className="mb-3">
                        {blog.tags.slice(0, 3).map(tag => (
                          <Badge 
                            key={tag} 
                            bg="light" 
                            text={document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'muted'}
                            className="me-1 mb-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <Button 
                        as={Link} 
                        to={`/blog/${blog.id}`} 
                        variant="outline-primary" 
                        className="w-100"
                      >
                        Read More
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center py-5">
            <h3>No blog posts found matching your criteria.</h3>
            <p className="text-muted mb-4">Try adjusting your search or filter to find what you're looking for.</p>
            <Button 
              variant="outline-primary"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('All');
                setSelectedTags([]);
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}

        {/* Pagination - Would be implemented with real API */}
        {filteredBlogs.length > 0 && (
          <div className="d-flex justify-content-center mt-5">
            <nav aria-label="Blog pagination">
              <ul className="pagination">
                <li className="page-item disabled">
                  <span className="page-link">Previous</span>
                </li>
                <li className="page-item active"><span className="page-link">1</span></li>
                <li className="page-item"><button type="button" className="page-link">2</button></li>
                <li className="page-item"><button type="button" className="page-link">3</button></li>
                <li className="page-item">
                  <button type="button" className="page-link">Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </Container>
    </div>
  );
};

export default Blogs;
